import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useOrganizationBranding } from '@/hooks/useOrganizationBranding';

const magicLinkSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type MagicLinkForm = z.infer<typeof magicLinkSchema>;

interface RequestState {
  status: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
}

export function MagicLinkRequest() {
  const [requestState, setRequestState] = useState<RequestState>({ status: 'idle' });
  const { name: orgName, logoUrl, brandColorHex, isLoading } = useOrganizationBranding();
  const { t } = useTranslation();

  useEffect(() => {
    // Apply brand color CSS variable
    if (brandColorHex) {
      document.documentElement.style.setProperty('--brand-color', brandColorHex);
    }
  }, [brandColorHex]);

  const form = useForm<MagicLinkForm>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: MagicLinkForm) => {
    try {
      setRequestState({ status: 'loading' });

      console.log('Requesting magic link for:', data.email);

      const { data: response, error } = await supabase.functions.invoke('generate-magic-link', {
        body: { 
          email: data.email,
          expiryMinutes: 30,
          redirectUrl: `${window.location.origin}/portal`
        }
      });

      if (error) {
        console.error('Magic link request error:', error);
        
        if (error.message?.includes('not found')) {
          setRequestState({
            status: 'error',
            message: t('magicLinkRequest.errors.notFound')
          });
        } else {
          setRequestState({
            status: 'error',
            message: error.message || t('magicLinkRequest.errors.sendFailed')
          });
        }
        return;
      }

      if (response?.success) {
        setRequestState({
          status: 'success',
          message: t('magicLinkRequest.success.message', { email: data.email })
        });
        form.reset();
      } else {
        setRequestState({
          status: 'error',
          message: t('magicLinkRequest.errors.sendFailed')
        });
      }
    } catch (error: any) {
      console.error('Request error:', error);
      setRequestState({
        status: 'error',
        message: t('magicLinkRequest.errors.unexpected')
      });
    }
  };

  const handleReset = () => {
    setRequestState({ status: 'idle' });
    form.reset();
  };

  if (requestState.status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/50 to-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              {logoUrl ? (
                <img src={logoUrl} alt={orgName} className="h-16 w-16 object-contain rounded-lg" />
              ) : (
                <div className="bg-green-100 rounded-full p-3">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              )}
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-green-800">{t('magicLinkRequest.success.title')}</CardTitle>
              <CardDescription className="text-base mt-2">
                {t('magicLinkRequest.success.subtitle')}
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <Mail className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {requestState.message}
              </AlertDescription>
            </Alert>

            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                <strong>{t('magicLinkRequest.success.nextSteps')}</strong>
              </p>
              <ol className="text-sm text-left space-y-1">
                <li>{t('magicLinkRequest.success.step1')}</li>
                <li>{t('magicLinkRequest.success.step2', { orgName })}</li>
                <li>{t('magicLinkRequest.success.step3')}</li>
                <li>{t('magicLinkRequest.success.step4')}</li>
              </ol>
            </div>

            <div className="space-y-2">
              <Button onClick={handleReset} variant="outline" className="w-full">
                {t('magicLinkRequest.success.sendAnotherButton')}
              </Button>
              <Button 
                variant="ghost" 
                className="w-full"
                onClick={handleReset}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('magicLinkRequest.success.tryAgainButton')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/50 to-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className="h-16 w-16 object-contain rounded-lg" />
            ) : (
              <div 
                className="rounded-full flex items-center justify-center text-white font-bold text-2xl w-16 h-16"
                style={{ backgroundColor: brandColorHex || '#EC4899' }}
              >
                {orgName?.charAt(0) || 'S'}
              </div>
            )}
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">{t('magicLinkRequest.title', { orgName })}</CardTitle>
            <CardDescription className="text-base mt-2">
              {t('magicLinkRequest.subtitle', { orgName })}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('magicLinkRequest.emailLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('magicLinkRequest.emailPlaceholder')}
                        type="email"
                        disabled={requestState.status === 'loading'}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {requestState.status === 'error' && requestState.message && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {requestState.message}
                  </AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                disabled={requestState.status === 'loading'}
              >
                {requestState.status === 'loading' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('magicLinkRequest.sendingButton')}
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    {t('magicLinkRequest.sendButton')}
                  </>
                )}
              </Button>
            </form>
          </Form>


          <div className="mt-4 text-center">
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">
                {t('security.info')}
                <br />
                {t('security.magicLinkExpiry')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}